/*
 * Site Component
 *
 * The Site component uses the twin model coordinate system to determine what context the twin data should be configured to,
 * in order to adopt the correct world space map position and orientation within the Mapbox context.
 *
 * the coordinate systems currently in use are "COORD_LOCAL" ( for everything ) | "COORD_WGS84" ( for nothing ).
 */

import { PointList, TwinEntity } from '@repo/backend-types';
import { ReactNode, useEffect, useMemo } from 'react';
import { degreesToRadians } from '@repo/ui';
import { useTwinContext } from '@repo/front-end-state';
import { WallsAndFloor } from './SiteBoundary';
import { HeatmapFloor } from '../../behaviours/HeatmapFloor';
import { setLiveAnchorOrientation } from '../../planView/liveEditFlightScene';

interface Props {
  entity: TwinEntity;
  children?: ReactNode;
  orientation: number;
}

/**
 * Site Component
 *
 * The Site component uses the twin model coordinate system to determine what context the twin data should be configured to,
 * in order to adopt the correct world space map position and orientation within the Mapbox context.
 *
 * the coordinate systems currently in use are "COORD_LOCAL" ( Newport, Movico ).
 */

const Site = ({ entity, children, orientation }: Props) => {
  // let orientation = entity.rotationY ?? 0;

  // Multifloor twins mount one heatmap plane PER FLOOR (inside each `Floor`'s altitude
  // group), so the site level must NOT also mount one — else upper-floor entities would
  // project down onto a site-level plane (the multifloor bug this spec fixes). Single-floor
  // twins keep the site-level plane exactly as before — including a one-floor twin whose plan now
  // writes its LONE floor (`floor-placement`): `multifloor` means two or more FLOORs.
  // Assumes the multifloor structure is SITE → FLOOR → areas (true for III-WIM / TFN-WMS), so a
  // mixed tree with AREAs hung directly under the site would leave those site-level areas uncovered
  // (no plane) — strictly safer than v1's down-projection, but a known limitation if such a twin
  // appears.
  const { multifloor } = useTwinContext();

  if (entity.bID === 'station-gym') {
    orientation -= 10.3;
  } // hack, because it's changed for Newport and I'm not sure why
  const rotation: [number, number, number] = [0, degreesToRadians(orientation), 0];

  // The turn the live twin is drawn at, for the Edit flight's plan-view pose
  // (`spec-onboarding-persistent-scene-edit-flight`, R2.3).
  useEffect(() => {
    setLiveAnchorOrientation(orientation);
    return () => setLiveAnchorOrientation(null);
  }, [orientation]);

  // Require at least one polygon: a `boundaries` object with an empty `polygons` array would crash
  // the site path (`.reduce` with no seed, and `WallsAndFloor`'s `boundingGeometry[0]`), so route
  // that malformed case to the derived path.
  const hasSiteBoundary = !!entity.boundaries?.polygons?.length;

  // The site boundary polygon that `WallsAndFloor` builds the walls/floor from (boundaried
  // twins only). Memoised on `entity` for a stable array identity across Site re-renders.
  // `useMemo` must sit above the branch (rules of hooks); `[]` on the boundary-less path (the
  // gym twins), where `WallsAndFloor` is not rendered. The R7 heatmap no longer needs this —
  // the splat model derives its own bounding plane from the entity footprints it collects.
  const boundingGeometry = useMemo<PointList[]>(
    () =>
      hasSiteBoundary
        ? entity
            .boundaries!.polygons.map(polygon => polygon.coordinates)
            .reduce((a, b) => a.concat(b), [] as PointList[])
        : [],
    [entity, hasSiteBoundary],
  );

  if (hasSiteBoundary) {
    return (
      <group rotation={rotation}>
        <WallsAndFloor boundingGeometry={boundingGeometry} />
        {/* R7 splat heatmap: soft per-entity blobs when the metric/mode is configured HEATMAP;
            renders nothing (box behaviour) otherwise. Sits between the floor (y=0) and the
            content group (y=+0.01). Single-floor only — a multifloor twin mounts per-floor
            planes inside each `Floor` instead. */}
        {!multifloor && <HeatmapFloor />}
        <group position={[0, 0.01, 0]}>{children}</group>
      </group>
    );
  } else {
    // Boundary-less (gym) twins: no `WallsAndFloor` (behaviour unchanged), but the R7 heatmap
    // still mounts. The `HeatmapFloor` wrapper self-gates to null unless the (metric, mode)
    // behaviour is HEATMAP, so box modes render exactly as before; children keep their original
    // (offset-free) placement in the rotated group.
    return (
      <>
        {children && (
          <group rotation={rotation}>
            {!multifloor && <HeatmapFloor />}
            {children}
          </group>
        )}
      </>
    );
  }
};

export { Site };
